import React, { useState, useEffect, useCallback } from 'react';
import { useDispatch } from 'react-redux';
import {
    Dialog,
    DialogContent,
    DialogActions,
    Box,
    Typography,
    Button,
    IconButton,
    Divider,
    makeStyles,
    Paper,
    Grid,
    Chip,
} from '@material-ui/core';
import CloseIcon from '@material-ui/icons/Close';
import CheckCircleIcon from '@material-ui/icons/CheckCircle';
import LocationOnIcon from '@material-ui/icons/LocationOn';
import { Spinner, LightBoxImage } from '../UI';
import {
    getSingleStaff,
    getKycDetails,
    triggerBGVManual,
    initiatePanVerification,
} from '../../store/actions/staffActionCreator';
import { enqueueSnackbar } from '../../store/actions/notificationActionCreator';
import RetriggerBGVModal from './RetriggerBGVModal';
import { checkHttpStatus } from '../../utils/helper';
import {
    isBlueDartOrg,
    isBlueDartOpsRole,
    isBlueDartRiderViewer,
} from '../../utils/appHelper';
import { KYCDocumentCard, KYCApprovalModal } from '../KYC';
import type { KYCDocument } from '../KYC';
import BackgroundVerification from '../Containers/BGVStatus';
import moment from 'moment';
import { KycRevealProvider } from '../KYC/reveal';

interface RiderViewModalProps {
    open: boolean;
    selectedRider: any;
    onClose: () => void;
}

interface ApprovalModalState {
    open: boolean;
    documentId: number;
    approvalStatus: 'APPROVED' | 'REJECTED';
}

const useStyles = makeStyles((theme: any) => ({
    dialogTitle: {
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        padding: '20px 24px',
        borderBottom: '1px solid rgba(38, 50, 56, 0.12)',
    },
    titleText: {
        fontSize: '20px',
        fontWeight: 700,
        color: '#14112d',
    },
    subtitleText: {
        fontSize: '14px',
        color: '#808080',
        marginTop: '4px',
    },
    dialogContent: {
        padding: '24px',
        minHeight: '400px',
    },
    sectionTitle: {
        fontSize: '18px',
        fontWeight: 700,
        color: '#14112d',
        marginBottom: '16px',
        display: 'flex',
        alignItems: 'center',
    },
    dividerLine: {
        flex: 1,
        marginLeft: '12px',
    },
    profileSection: {
        display: 'flex',
        alignItems: 'flex-start',
        gap: '24px',
        padding: '16px',
        marginBottom: '24px',
        borderRadius: '12px',
        border: '1px solid rgba(38, 50, 56, 0.12)',
        backgroundColor: '#fafafa',
    },
    profileImageContainer: {
        width: '100px',
        height: '100px',
        borderRadius: '12px',
        overflow: 'hidden',
        border: '2px solid rgba(38, 50, 56, 0.16)',
        backgroundColor: '#fff',
    },
    profileImage: {
        width: '100%',
        height: '100%',
        objectFit: 'cover',
    },
    profilePlaceholder: {
        width: '100%',
        height: '100%',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        backgroundColor: '#e0e0e0',
        color: '#9e9e9e',
        fontSize: '14px',
    },
    profileDetails: {
        flex: 1,
    },
    profileName: {
        fontSize: '18px',
        fontWeight: 700,
        color: '#14112d',
        marginBottom: '4px',
    },
    profileSubtext: {
        fontSize: '14px',
        color: '#808080',
        marginBottom: '12px',
    },
    basicInfoGrid: {
        display: 'grid',
        gridTemplateColumns: 'repeat(3, 1fr)',
        gap: '16px',
    },
    fieldLabel: {
        fontSize: '12px',
        color: 'rgba(20, 17, 45, 0.64)',
        fontWeight: 500,
        marginBottom: '2px',
    },
    fieldValue: {
        fontSize: '14px',
        color: '#14112d',
        fontWeight: 600,
    },
    kycSection: {
        marginTop: '24px',
    },
    dialogActions: {
        padding: '16px 24px',
        borderTop: '1px solid rgba(38, 50, 56, 0.12)',
    },
}));

const RiderViewModal: React.FC<RiderViewModalProps> = ({
    open,
    selectedRider,
    onClose,
}) => {
    const classes = useStyles();
    const dispatch = useDispatch();
    const [loading, setLoading] = useState(false);
    const [staffInfo, setStaffInfo] = useState<any>(null);
    const [kycInfo, setKycInfo] = useState<any>(null);
    const [approvalModal, setApprovalModal] = useState<ApprovalModalState>({
        open: false,
        documentId: 0,
        approvalStatus: 'APPROVED',
    });
    const [retriggerBGVOpen, setRetriggerBGVOpen] = useState(false);

    const dispatchGetSingleStaff = useCallback(
        (id: any, onSuccess: Function, onError: Function) => {
            dispatch(getSingleStaff(id, onSuccess, onError));
        },
        [dispatch],
    );

    const dispatchGetKycDetails = useCallback(
        (id: any, onSuccess: Function, onError: Function) => {
            dispatch(getKycDetails(id, onSuccess, onError));
        },
        [dispatch],
    );

    useEffect(() => {
        if (open && selectedRider?.id) {
            fetchRiderDetails();
        }
    }, [open, selectedRider?.id]);

    const fetchRiderDetails = () => {
        setLoading(true);
        dispatchGetSingleStaff(
            selectedRider.id,
            (resp: any) => {
                if (checkHttpStatus(resp?.status)) {
                    setStaffInfo(resp?.response);
                }
                dispatchGetKycDetails(
                    selectedRider.id,
                    (kycResp: any) => {
                        if (checkHttpStatus(kycResp?.status)) {
                            setKycInfo(kycResp?.response);
                        }
                        setLoading(false);
                    },
                    () => {
                        setLoading(false);
                    },
                );
            },
            () => {
                setLoading(false);
            },
        );
    };

    const refreshKycDetails = () => {
        dispatchGetKycDetails(
            selectedRider.id,
            (kycResp: any) => {
                if (checkHttpStatus(kycResp?.status)) {
                    setKycInfo(kycResp?.response);
                }
            },
            () => {},
        );
    };

    const handleClose = () => {
        setStaffInfo(null);
        setKycInfo(null);
        onClose();
    };

    // A Rider Viewer reads; every approve/reject control is hidden for them. The API refuses
    // these too, so leaving the buttons visible would only produce a 403 on click. Vendors keep
    // their own approval step, and non-Blue-Dart orgs are untouched.
    const kycReadOnly = isBlueDartRiderViewer();

    const handleApprove = (documentId: number) => {
        setApprovalModal({
            open: true,
            documentId,
            approvalStatus: 'APPROVED',
        });
    };

    const handleReject = (documentId: number) => {
        setApprovalModal({
            open: true,
            documentId,
            approvalStatus: 'REJECTED',
        });
    };

    const handleApprovalModalClose = () => {
        setApprovalModal({
            open: false,
            documentId: 0,
            approvalStatus: 'APPROVED',
        });
    };

    const handleApprovalSuccess = () => {
        refreshKycDetails();
    };

    const showMessage = useCallback(
        (msg: string, type: 'success' | 'error' = 'error') => {
            dispatch(
                enqueueSnackbar({
                    message: msg,
                    options: { key: Date.now(), variant: type },
                }),
            );
        },
        [dispatch],
    );

    const handleRetriggerBGV = (address: string) => {
        setLoading(true);
        dispatch(
            triggerBGVManual(
                { userId: selectedRider?.id, address },
                () => {
                    setLoading(false);
                    setRetriggerBGVOpen(false);
                    showMessage('BGV initiated successfully', 'success');
                    refreshKycDetails();
                },
                (err: any) => {
                    setLoading(false);
                    const errorMessage =
                        err?.status?.code === 400 && err?.status?.message
                            ? err.status.message
                            : err?.message || 'BGV initiation failed';
                    showMessage(errorMessage, 'error');
                },
            ) as any,
        );
    };

    const handleInitiateBGV = () => {
        setLoading(true);
        dispatch(
            initiatePanVerification(
                selectedRider?.id,
                () => {
                    setLoading(false);
                    showMessage('BGV initiated successfully', 'success');
                    refreshKycDetails();
                },
                (err: any) => {
                    setLoading(false);
                    const errorMessage =
                        err?.status?.code === 400 && err?.status?.message
                            ? err.status.message
                            : err?.message || 'BGV initiation failed';
                    showMessage(errorMessage, 'error');
                },
            ) as any,
        );
    };

    const formatDate = (value: any) => {
        if (!value) return '-';
        return moment(value).format('DD/MM/YYYY');
    };

    const getProfileImage = () => {
        return kycInfo?.selfie?.image || kycInfo?.s3ProfileLink || null;
    };

    const getAadharDocument = (): KYCDocument | null => {
        return kycInfo?.aadhar || null;
    };

    const getPanDocument = (): KYCDocument | null => {
        return kycInfo?.panCard || null;
    };

    const getDLDocument = (): KYCDocument | null => {
        return kycInfo?.drivingLicense || null;
    };

    const getRCDocument = (): KYCDocument | null => {
        return kycInfo?.rc || null;
    };

    const renderProfileSection = () => {
        const profileImage = getProfileImage();
        const name =
            staffInfo?.name || kycInfo?.name || selectedRider?.name || '-';
        const phone = staffInfo?.contactNumber || selectedRider?.phone || '-';
        const staffCode = staffInfo?.staffCode || kycInfo?.staffUniqueId || '-';

        return (
            <Box className={classes.profileSection}>
                <Box className={classes.profileImageContainer}>
                    {profileImage ? (
                        <LightBoxImage
                            source={profileImage}
                            containerStyles={{
                                width: '100%',
                                height: '100%',
                            }}
                            imageStyles={{
                                width: '100%',
                                height: '100%',
                                objectFit: 'cover',
                                borderRadius: '10px',
                            }}
                        />
                    ) : (
                        <Box className={classes.profilePlaceholder}>
                            No Photo
                        </Box>
                    )}
                </Box>

                <Box className={classes.profileDetails}>
                    <Typography className={classes.profileName}>
                        {name}
                    </Typography>
                    <Typography className={classes.profileSubtext}>
                        {phone} | {staffCode}
                    </Typography>

                    <Box className={classes.basicInfoGrid}>
                        <Box>
                            <Typography className={classes.fieldLabel}>
                                Hub Name
                            </Typography>
                            <Typography className={classes.fieldValue}>
                                {staffInfo?.hubName || kycInfo?.hubName || '-'}
                            </Typography>
                        </Box>
                        <Box>
                            <Typography className={classes.fieldLabel}>
                                City
                            </Typography>
                            <Typography className={classes.fieldValue}>
                                {selectedRider?.city || staffInfo?.city || '-'}
                            </Typography>
                        </Box>
                        <Box>
                            <Typography className={classes.fieldLabel}>
                                Date of Birth
                            </Typography>
                            <Typography className={classes.fieldValue}>
                                {kycInfo?.dob ||
                                    (staffInfo?.dateOfBirthEpochMillis
                                        ? formatDate(
                                              staffInfo.dateOfBirthEpochMillis,
                                          )
                                        : '-')}
                            </Typography>
                        </Box>
                        <Box>
                            <Typography className={classes.fieldLabel}>
                                Date of Joining
                            </Typography>
                            <Typography className={classes.fieldValue}>
                                {staffInfo?.joiningDateEpochMillis
                                    ? formatDate(
                                          staffInfo.joiningDateEpochMillis,
                                      )
                                    : '-'}
                            </Typography>
                        </Box>
                        <Box>
                            <Typography className={classes.fieldLabel}>
                                Vehicle Type
                            </Typography>
                            <Typography className={classes.fieldValue}>
                                {kycInfo?.vehicleType || '-'}
                            </Typography>
                        </Box>
                        <Box>
                            <Typography className={classes.fieldLabel}>
                                Status
                            </Typography>
                            <Typography className={classes.fieldValue}>
                                {selectedRider?.status?.replace(/_/g, ' ') ||
                                    '-'}
                            </Typography>
                        </Box>
                    </Box>
                </Box>
            </Box>
        );
    };

    const renderHomeSelfieSection = () => {
        const selfie = kycInfo?.homeSelfie;
        if (!selfie?.id) return null;

        const hasLocation = selfie.latitude != null && selfie.longitude != null;
        const confidence = isBlueDartOrg()
            ? Number(selfie.faceMatchScore) > 0
                ? selfie.faceMatchScore
                : null
            : selfie.faceMatchConfidence;
        const confidenceStyle =
            confidence == null
                ? { bg: '#f5f5f5', text: '#9e9e9e', label: 'N/A' }
                : confidence >= 85
                  ? { bg: '#e8f5e9', text: '#2e7d32', label: 'High' }
                  : confidence >= 65
                    ? { bg: '#fff8e1', text: '#f57f17', label: 'Medium' }
                    : { bg: '#ffebee', text: '#c62828', label: 'Low' };

        return (
            <Box mt={3}>
                <Box display="flex" alignItems="center" mb={2}>
                    <Typography className={classes.sectionTitle}>
                        Home Selfie
                    </Typography>
                    <Divider className={classes.dividerLine} />
                </Box>
                <Paper
                    elevation={0}
                    style={{
                        border: '1px solid rgba(38,50,56,0.16)',
                        borderRadius: 12,
                        padding: 16,
                    }}
                >
                    <Grid container spacing={3} alignItems="flex-start">
                        <Grid item>
                            <Box
                                style={{
                                    width: 120,
                                    height: 120,
                                    borderRadius: 10,
                                    overflow: 'hidden',
                                    border: '1px solid rgba(38,50,56,0.16)',
                                    background: '#f5f5f5',
                                }}
                            >
                                {selfie.image ? (
                                    <LightBoxImage
                                        source={selfie.image}
                                        containerStyles={{
                                            width: 120,
                                            height: 120,
                                        }}
                                        imageStyles={{
                                            width: 120,
                                            height: 120,
                                            objectFit: 'cover',
                                            borderRadius: 10,
                                        }}
                                    />
                                ) : (
                                    <Box
                                        display="flex"
                                        alignItems="center"
                                        justifyContent="center"
                                        style={{
                                            width: '100%',
                                            height: '100%',
                                        }}
                                    >
                                        <Typography
                                            style={{
                                                color: '#bdbdbd',
                                                fontSize: 12,
                                            }}
                                        >
                                            No Image
                                        </Typography>
                                    </Box>
                                )}
                            </Box>
                        </Grid>

                        <Grid item xs>
                            <Grid container spacing={2}>
                                <Grid item xs={12} sm={4}>
                                    <Typography
                                        style={{
                                            fontSize: 12,
                                            color: 'rgba(20,17,45,0.64)',
                                            fontWeight: 500,
                                            marginBottom: 4,
                                        }}
                                    >
                                        Face Match Score
                                    </Typography>
                                    <Box
                                        display="inline-flex"
                                        alignItems="center"
                                        style={{
                                            gap: 6,
                                            padding: '5px 14px',
                                            borderRadius: 20,
                                            background: confidenceStyle.bg,
                                            color: confidenceStyle.text,
                                            fontWeight: 700,
                                            fontSize: 13,
                                        }}
                                    >
                                        <span>
                                            {confidence != null
                                                ? `${confidence}%`
                                                : 'N/A'}
                                        </span>
                                        <span
                                            style={{
                                                fontSize: 11,
                                                opacity: 0.8,
                                            }}
                                        >
                                            ({confidenceStyle.label})
                                        </span>
                                    </Box>
                                </Grid>

                                <Grid item xs={12} sm={8}>
                                    <Typography
                                        style={{
                                            fontSize: 12,
                                            color: 'rgba(20,17,45,0.64)',
                                            fontWeight: 500,
                                            marginBottom: 4,
                                        }}
                                    >
                                        Location
                                    </Typography>
                                    <Box
                                        display="flex"
                                        alignItems="flex-start"
                                        style={{ marginTop: 4 }}
                                    >
                                        <Box
                                            display="flex"
                                            flexDirection="column"
                                            alignItems="flex-start"
                                        >
                                            {hasLocation ? (
                                                <Button
                                                    variant="outlined"
                                                    size="small"
                                                    style={{
                                                        borderColor: '#1976d2',
                                                        color: '#1976d2',
                                                        marginRight: 12,
                                                        textTransform: 'none',
                                                    }}
                                                    startIcon={
                                                        <LocationOnIcon
                                                            style={{
                                                                fontSize: 16,
                                                            }}
                                                        />
                                                    }
                                                    onClick={() =>
                                                        window.open(
                                                            `https://www.google.com/maps?q=${selfie.latitude},${selfie.longitude}`,
                                                            '_blank',
                                                            'noopener,noreferrer',
                                                        )
                                                    }
                                                >
                                                    View in Map
                                                </Button>
                                            ) : (
                                                <Typography
                                                    style={{
                                                        fontSize: 14,
                                                        color: '#9e9e9e',
                                                    }}
                                                >
                                                    —
                                                </Typography>
                                            )}
                                            {!!selfie.address && (
                                                <Typography
                                                    style={{
                                                        fontSize: 12,
                                                        color: '#555',
                                                        marginTop: 6,
                                                        maxWidth: 260,
                                                        wordBreak: 'break-word',
                                                    }}
                                                >
                                                    {selfie.address}
                                                </Typography>
                                            )}
                                        </Box>
                                        {selfie.addressVerified && (
                                            <Box style={{ marginLeft: 8 }}>
                                                <Chip
                                                    icon={
                                                        <CheckCircleIcon
                                                            style={{
                                                                fontSize: 16,
                                                                color: '#2e7d32',
                                                            }}
                                                        />
                                                    }
                                                    label="Address Verified"
                                                    size="small"
                                                    style={{
                                                        background: '#e8f5e9',
                                                        color: '#2e7d32',
                                                        fontWeight: 700,
                                                        fontSize: 13,
                                                        borderRadius: 20,
                                                    }}
                                                />
                                            </Box>
                                        )}
                                    </Box>
                                </Grid>
                            </Grid>
                        </Grid>
                    </Grid>
                </Paper>
            </Box>
        );
    };

    const renderKYCSection = () => {
        const aadhar = getAadharDocument();
        const pan = getPanDocument();
        const dl = getDLDocument();
        const rc = getRCDocument();

        // Check if any document exists
        const hasAadhar = aadhar && aadhar.id;
        const hasPan = pan && pan.id;
        const hasDL = dl && dl.id;
        const hasRC = rc && rc?.id;
        const hasAnyDocument = hasAadhar || hasPan || hasDL || hasRC;
        let userStatus = selectedRider?.status ?? null;
        return (
            <KycRevealProvider
                entityType="USER"
                entityId={selectedRider?.id}
                onRevealed={refreshKycDetails}
            >
                <Box className={classes.kycSection}>
                    <Box display="flex" alignItems="center" mb={2}>
                        <Typography className={classes.sectionTitle}>
                            KYC Documents
                        </Typography>
                        <Divider className={classes.dividerLine} />
                    </Box>

                    {hasAnyDocument ? (
                        <>
                            {(hasAadhar || !kycInfo?.autoVerified) && (
                                <KYCDocumentCard
                                    title="Aadhaar Card"
                                    document={aadhar}
                                    documentType="AADHAAR"
                                    showBackImage={true}
                                    onApprove={handleApprove}
                                    onReject={handleReject}
                                    readOnly={kycReadOnly}
                                    userStatus={userStatus}
                                />
                            )}

                            {(hasPan || !kycInfo?.autoVerified) && (
                                <KYCDocumentCard
                                    title="PAN Card"
                                    document={pan}
                                    documentType="PAN"
                                    showBackImage={false}
                                    onApprove={handleApprove}
                                    onReject={handleReject}
                                    readOnly={kycReadOnly}
                                    userStatus={userStatus}
                                />
                            )}

                            {(hasDL || !kycInfo?.autoVerified) && (
                                <KYCDocumentCard
                                    title="Driving License"
                                    document={dl}
                                    documentType="DL"
                                    showBackImage={true}
                                    onApprove={handleApprove}
                                    onReject={handleReject}
                                    readOnly={kycReadOnly}
                                    userStatus={userStatus}
                                />
                            )}
                            {(hasRC || !kycInfo?.autoVerified) && (
                                <KYCDocumentCard
                                    title="RC Book"
                                    document={rc}
                                    documentType="RC"
                                    showBackImage={true}
                                    onApprove={handleApprove}
                                    onReject={handleReject}
                                    readOnly={kycReadOnly}
                                    userStatus={userStatus}
                                />
                            )}
                        </>
                    ) : (
                        <Typography
                            style={{ color: '#9e9e9e', fontStyle: 'italic' }}
                        >
                            No KYC documents available
                        </Typography>
                    )}
                </Box>
            </KycRevealProvider>
        );
    };

    return (
        <>
            <Dialog
                open={open}
                onClose={handleClose}
                fullWidth
                maxWidth="md"
                aria-labelledby="rider-view-dialog-title"
            >
                <Box className={classes.dialogTitle}>
                    <Box>
                        <Typography className={classes.titleText}>
                            Rider Details
                        </Typography>
                        {selectedRider && (
                            <Typography className={classes.subtitleText}>
                                {selectedRider?.name} -{' '}
                                {selectedRider?.staffCode ||
                                    selectedRider?.phone}
                            </Typography>
                        )}
                    </Box>
                    <IconButton
                        aria-label="close"
                        onClick={handleClose}
                        size="small"
                    >
                        <CloseIcon />
                    </IconButton>
                </Box>

                <DialogContent className={classes.dialogContent}>
                    <Spinner loading={loading}>
                        {renderProfileSection()}
                        {renderHomeSelfieSection()}
                        {renderKYCSection()}
                        {kycInfo?.bgvStatus?.length > 0 && (
                            <BackgroundVerification
                                bgvStatusList={kycInfo.bgvStatus}
                            />
                        )}
                        {(() => {
                            const BGV_NO_INITIATE = [
                                'INITIATED',
                                'VERIFIED',
                                'FAILED',
                                'Insufficient',
                            ];
                            const bgvStatusList = kycInfo?.bgvStatus ?? [];
                            const hasInsufficientBGV = bgvStatusList.some(
                                (item: any) =>
                                    (item?.caseSeverity || '')
                                        .trim()
                                        .toLowerCase() === 'insufficient' ||
                                    (item?.status || '')
                                        .trim()
                                        .toLowerCase() === 'insufficient',
                            );
                            const userBgvStatus = kycInfo?.userBgvStatus ?? '';
                            const panApproved =
                                kycInfo?.panCard?.approvalStatus === 'APPROVED';
                            const aadhaarApproved =
                                kycInfo?.aadhar?.approvalStatus === 'APPROVED';
                            const dlDoc = kycInfo?.drivingLicense;
                            const dlRequiredForBGV =
                                kycInfo?.dlRequiredForBGV === true;
                            const dlApprovedOrNotRequired =
                                !dlRequiredForBGV ||
                                (!!dlDoc?.id &&
                                    dlDoc?.approvalStatus === 'APPROVED');
                            const riderStatus =
                                staffInfo?.status ??
                                selectedRider?.status ??
                                '';
                            const showInitiateBGV =
                                isBlueDartOrg() &&
                                isBlueDartOpsRole() &&
                                riderStatus === 'PENDING_BD_OPS_APPROVAL' &&
                                panApproved &&
                                aadhaarApproved &&
                                dlApprovedOrNotRequired &&
                                !BGV_NO_INITIATE.includes(userBgvStatus) &&
                                !hasInsufficientBGV;
                            const showRetriggerBGV =
                                isBlueDartOrg() &&
                                isBlueDartOpsRole() &&
                                hasInsufficientBGV &&
                                riderStatus !== 'APPROVED' &&
                                riderStatus !== 'REJECTED';
                            if (!showInitiateBGV && !showRetriggerBGV)
                                return null;
                            return (
                                <Box
                                    display="flex"
                                    justifyContent="flex-end"
                                    mt={2}
                                    style={{ gap: 8 }}
                                >
                                    {showInitiateBGV && (
                                        <Button
                                            variant="contained"
                                            style={{
                                                background: '#43419E',
                                                color: '#fff',
                                            }}
                                            onClick={handleInitiateBGV}
                                        >
                                            Initiate BGV
                                        </Button>
                                    )}
                                    {showRetriggerBGV && (
                                        <Button
                                            variant="contained"
                                            style={{
                                                background: '#43419E',
                                                color: '#fff',
                                            }}
                                            onClick={() =>
                                                setRetriggerBGVOpen(true)
                                            }
                                        >
                                            Retrigger BGV
                                        </Button>
                                    )}
                                </Box>
                            );
                        })()}
                    </Spinner>
                </DialogContent>

                <DialogActions className={classes.dialogActions}>
                    <Button
                        onClick={handleClose}
                        variant="outlined"
                        color="primary"
                    >
                        Close
                    </Button>
                </DialogActions>
            </Dialog>

            {/* KYC Approval Modal */}
            <KYCApprovalModal
                open={approvalModal.open}
                onClose={handleApprovalModalClose}
                staffId={selectedRider?.id}
                documentId={approvalModal.documentId}
                approvalStatus={approvalModal.approvalStatus}
                onSuccess={handleApprovalSuccess}
            />

            {retriggerBGVOpen && (
                <RetriggerBGVModal
                    open={retriggerBGVOpen}
                    onClose={() => setRetriggerBGVOpen(false)}
                    loading={loading}
                    name={staffInfo?.name}
                    contactNumber={staffInfo?.contactNumber}
                    dob={kycInfo?.aadhar?.autoKycDocumentDob}
                    defaultAddress={kycInfo?.uidaiAadhar?.address ?? ''}
                    onSubmit={handleRetriggerBGV}
                    title={
                        (kycInfo?.bgvStatus ?? []).some(
                            (item: any) =>
                                (item?.caseSeverity || '')
                                    .trim()
                                    .toLowerCase() === 'insufficient',
                        )
                            ? 'Retrigger Background Verification'
                            : 'Initiate Background Verification'
                    }
                />
            )}
        </>
    );
};

export default RiderViewModal;
